import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import { apiClient } from '../apiClient';

export interface Editorial {
  slug: string;
  editorial: string;
}

/**
 * Fetches a problem's editorial from the gated endpoint.
 *
 * `enabled` is the caller's unlock check and it gates the REQUEST, not just the
 * display. The server refuses an unsolved student with a 403, so firing this
 * unconditionally would put a red line in every student's network tab and tell
 * them there is something behind it — which is the opposite of withholding it.
 *
 * A 404 (no editorial was ever written for this problem, which is true of most
 * of the catalogue) is left to surface as a query error and rendered as a plain
 * "no editorial" empty state rather than retried.
 */
export function useEditorial(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.problems.editorial(slug),
    queryFn: async () => {
      const { data } = await apiClient.get<Editorial>(`/api/v1/problems/${slug}/editorial`);
      return data;
    },
    enabled: !!slug && enabled,
    retry: false,
    staleTime: 10 * 60_000,
  });
}
